// OrbitRings — thin warm-white torii that visualize each satellite's
// orbit radius around the planet. One ring per satellite, all sharing
// a single material instance so we don't allocate per-ring.
//
// The rings live inside the planet-world group, so they inherit the
// planet's scroll- and drag-driven rotation. The brief asks for the
// rings to "lay flat" — a default TorusGeometry sits in the XY plane,
// so we rotate π/2 around X to put it in the XZ plane (matching the
// satellites' orbital plane at y=0.2).
//
// Performance: each ring is one mesh, ~64 segments. With 4 rings we
// stay well under the instancing threshold and keep the build simple.
// `raycast` is disabled on every ring — they are decoration only.

import { useEffect, useMemo } from 'react';
import * as THREE from 'three';
import type { Group } from 'three';

import { SATELLITES } from './planetSections';

const RING_PALETTE = ['#efe8dc', '#9fd2b9', '#c6ad78'];
const RING_OPACITY = 0.10;
const RING_TUBE_RADIUS = 0.006; // very thin
const RING_RADIAL_SEGMENTS = 6;
const RING_TUBULAR_SEGMENTS = 128;

export interface OrbitRingsProps {
  /** Optional Y elevation to match the satellites' orbital plane. */
  elevation?: number;
}

export function OrbitRings({ elevation = 0.2 }: OrbitRingsProps) {
  // Build the radii list once. SATELLITES is module-level constant so
  // this never changes; useMemo just memoizes the array reference.
  const radii = useMemo(
    () => SATELLITES.map((s) => s.orbitRadius),
    [],
  );

  const materials = useMemo(() => {
    return RING_PALETTE.map((color) => (
      new THREE.MeshBasicMaterial({
        color,
        transparent: true,
        opacity: RING_OPACITY,
        depthWrite: false,
        side: THREE.DoubleSide,
        toneMapped: false,
      })
    ));
  }, []);

  useEffect(() => () => {
    materials.forEach((material) => material.dispose());
  }, [materials]);

  return (
    <group name="orbit-rings" position={[0, elevation, 0]} userData={{ pickable: false }}>
      {radii.map((r, i) => (
        <mesh
          key={`orbit-${SATELLITES[i].id}-${i}`}
          name={`orbit-ring-${SATELLITES[i].id}`}
          // Rotate the torus from its native XY plane into the XZ plane
          // so it appears as a flat ring at the satellites' orbital
          // height.
          rotation={[-Math.PI / 2, 0, 0]}
          // No raycast on rings — they are decoration only.
          raycast={() => null}
          userData={{ pickable: false }}
        >
          <torusGeometry args={[r, RING_TUBE_RADIUS, RING_RADIAL_SEGMENTS, RING_TUBULAR_SEGMENTS]} />
          <primitive object={materials[i % materials.length]} attach="material" />
        </mesh>
      ))}
    </group>
  );
}

export default OrbitRings;

// Augment the global Window type with a shared Group reference used by
// the SatelliteInteraction raycaster. The Satellites and NoteFragments
// modules set this when they mount.
declare global {
  interface Window {
    __planetInteractiveGroup?: Group | null;
  }
}
